/**
 * Pure parsing + shaping helpers for the `find_entities_near_location` tool
 * result. No React, no IO — the controller (FindEntitiesNearLocationToolUI)
 * and the presentational view both consume these.
 */

export interface FindEntitiesNearLocationArgs {
  location: string
  radius_km?: number
  entity_type?: string
}

export interface AnchorNeighbor {
  id: string
  name: string
  type: string
}

export interface LocationAnchor {
  anchor_id: string
  name: string
  type: string
  latitude: number
  longitude: number
  distance_meters?: number
  neighbors?: AnchorNeighbor[]
}

export interface AmbiguousCandidate {
  display?: string
  city?: string
  country?: string
  lat?: number
  lon?: number
}

export interface FindEntitiesNearLocationResult {
  status: 'ok' | 'empty' | 'error' | 'disabled' | 'ambiguous'
  location?: {
    name: string
    latitude: number
    longitude: number
  }
  radius_km?: number
  anchors?: LocationAnchor[]
  candidates?: AmbiguousCandidate[]
  error?: string
}

export function parseResult(
  raw: FindEntitiesNearLocationResult | string | undefined
): FindEntitiesNearLocationResult | null {
  if (raw == null) return null
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as FindEntitiesNearLocationResult
    } catch {
      return { status: 'error', error: raw }
    }
  }
  return raw
}

export function formatDistance(meters?: number): string | null {
  if (meters == null) return null
  if (meters < 1000) return `${Math.round(meters)} m`
  return `${(meters / 1000).toFixed(1)} km`
}

/**
 * The object set is anchors + their connected entities, so highlighting /
 * expansion can reach both.
 */
export function extractEntityIds(parsed: FindEntitiesNearLocationResult | null): string[] {
  const anchors = parsed?.anchors ?? []
  return [
    ...anchors.map(a => a.anchor_id),
    ...anchors.flatMap(a => (a.neighbors ?? []).map(n => n.id)),
  ]
}
